import * as React from "react";
import { useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, Field, PageHeader, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Panel, Section, StateView, useAuth, useToast } from "@ozpanel/ui";
import { Link } from "react-router";
import { Plus } from "lucide-react";
import { useCreate } from "../../useCreate";

// Authoritative DNS: the zones this node serves and the records in them. Shapes read live —
// note the zone name carries a TRAILING DOT (PowerDNS canonical form) and is used verbatim as
// the path segment.

interface Zone {
  name: string;
  kind: string;
  serial: number;
  dnssec: boolean;
}
interface RRSet {
  name: string;
  type: string;
  ttl: number;
  records: { content: string; disabled?: boolean }[] | string[] | null;
}
interface Nameservers {
  actual: string[] | null;
  plan?: { zone?: string; nameservers?: string[] | null };
}

function contentOf(r: RRSet): string[] {
  const rec = r.records ?? [];
  return rec.map((x) => (typeof x === "string" ? x : x.content));
}

export function DnsZonesScreen() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  // Creating and deleting a ZONE had no console at all — only the records inside one did. So a
  // zone could exist and be edited, and could only be brought into being (or removed) through the
  // API by hand.
  const [creating, setCreating] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState<Zone | null>(null);
  const zones = useQuery({
    queryKey: ["dns-zones"],
    queryFn: () => client.apiFetch<{ items: Zone[] | null; total: number }>("/dns/zones"),
  });

  const cols: DataListColumn<Zone>[] = [
    {
      key: "name",
      label: "Zone",
      sortable: true,
      render: (z) => (
        <Link className="text-accent hover:underline" to={`/dns/zones/${encodeURIComponent(z.name)}`}>
          {z.name}
        </Link>
      ),
    },
    { key: "kind", label: "Kind", sortable: true },
    { key: "serial", label: "Serial", sortable: true, value: (z) => z.serial, render: (z) => <span className="font-mono text-[12px]">{z.serial}</span> },
    {
      key: "dnssec",
      label: "DNSSEC",
      sortable: true,
      value: (z) => (z.dnssec ? "on" : "off"),
      render: (z) => <StatusPill tone={z.dnssec ? "ok" : "neutral"}>{z.dnssec ? "signed" : "unsigned"}</StatusPill>,
    },
  ];

  return (
    <div>
      <PageHeader
        title="DNS zones"
        subtitle={zones.data ? `${zones.data.total} served by this node` : undefined}
        actions={
          <Button size="sm" variant="accent" onClick={() => setCreating("")}>
            New zone
          </Button>
        }
      />
      <DataList
        items={zones.data?.items ?? []}
        columns={cols}
        getRowKey={(z) => z.name}
        searchable
        searchPlaceholder="Search zones…"
        loading={zones.isLoading}
        error={zones.error}
        errorContext="DNS zones"
        emptyMessage="No zones."
        emptyHint="A zone appears here once a domain is delegated to this platform's nameservers."
        rowActions={(z) => (
          <Button size="sm" variant="danger" onClick={() => setDeleting(z)}>
            delete
          </Button>
        )}
      />

      <PlatformNameservers />

      <ConfirmDialog
        open={creating !== null}
        onOpenChange={(o) => !o && setCreating(null)}
        title="Create a zone"
        confirmLabel="Create it"
        body={
          <div className="space-y-2">
            <Field
              mono
              label="Domain"
              value={creating ?? ""}
              placeholder="example.com"
              onChange={(v) => setCreating(v)}
            />
            <p className="text-[11.5px] leading-relaxed text-muted">
              The domain must already belong to an account here. The zone is created as PowerDNS
              Native with this platform&apos;s nameservers — it does not start answering for the
              world until the domain is delegated to them at the registrar.
            </p>
          </div>
        }
        onConfirm={async () => {
          const d = (creating ?? "").trim();
          setCreating(null);
          if (!d) return;
          try {
            await client.apiFetch("/dns/zones", { method: "POST", body: JSON.stringify({ domain: d }) });
            notify({ kind: "success", title: `${d} created` });
            void qc.invalidateQueries({ queryKey: ["dns-zones"] });
          } catch (e) {
            notify({ kind: "error", title: "Could not create it", body: e instanceof Error ? e.message : String(e) });
          }
        }}
      />

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={deleting ? `Delete the zone ${deleting.name}?` : ""}
        confirmLabel="Delete it"
        destructive
        body={
          <p>
            Every record in it goes with it, and this node stops answering for the name. Anything
            still pointing at it — a website, mail delivery — stops resolving as soon as caches
            expire. The domain itself and the account keep existing.
          </p>
        }
        onConfirm={async () => {
          const z = deleting;
          setDeleting(null);
          if (!z) return;
          try {
            await client.apiFetch(`/dns/zones/${encodeURIComponent(z.name)}`, { method: "DELETE" });
            notify({ kind: "success", title: `${z.name} deleted` });
            void qc.invalidateQueries({ queryKey: ["dns-zones"] });
          } catch (e) {
            notify({ kind: "error", title: "Could not delete it", body: e instanceof Error ? e.message : String(e) });
          }
        }}
      />
    </div>
  );
}

export function DnsZoneDetailScreen() {
  const { zone = "" } = useParams();
  const name = decodeURIComponent(zone);
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [del, setDel] = React.useState<RRSet | null>(null);

  const recs = useQuery({
    queryKey: ["dns-records", name],
    queryFn: () => client.apiFetch<{ records: RRSet[] | null; total: number; zone: string }>(`/dns/zones/${encodeURIComponent(name)}/records`),
  });
  const ns = useQuery({
    queryKey: ["dns-ns", name],
    queryFn: () => client.apiFetch<Nameservers>(`/dns/zones/${encodeURIComponent(name)}/nameservers`),
  });

  const create = useCreate({
    title: `Add a record to ${name}`,
    description:
      "The name may be relative to the zone or fully qualified. Multiple values for one name and type belong on separate lines — DNS treats them as one set.",
    submitLabel: "Add record",
    fields: [
      { key: "name", label: "Name", required: true, placeholder: "www  (or @ for the zone itself)" },
      {
        key: "type",
        label: "Type",
        type: "enum",
        options: ["A", "AAAA", "CNAME", "MX", "TXT", "SRV", "CAA", "NS"].map((t) => ({ value: t, label: t })),
        defaultValue: "A",
      },
      { key: "content", label: "Value", required: true, placeholder: "203.0.113.10" },
      { key: "ttl", label: "TTL (seconds)", type: "int", defaultValue: "300" },
    ],
    run: (v) =>
      client.apiFetch<unknown>(`/dns/zones/${encodeURIComponent(name)}/records`, {
        method: "POST",
        body: JSON.stringify({ name: v.name, type: v.type, content: v.content, ttl: Number(v.ttl || 300) }),
      }),
    invalidates: [["dns-records", name]],
    success: (v) => `${v.name} ${v.type} added`,
  });

  // DELETE takes QUERY PARAMETERS, not a JSON body — the create on the same path takes a body,
  // which is why sending one here looks obviously right and fails. Measured on node10: a body
  // answered "type is required" while the record stayed, so a delete that reported failure had
  // in fact done nothing, and the record survived on a live zone.
  const remove = useMutation({
    mutationFn: (r: RRSet) =>
      client.apiFetch<unknown>(
        `/dns/zones/${encodeURIComponent(name)}/records?name=${encodeURIComponent(r.name)}&type=${encodeURIComponent(r.type)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      notify({ kind: "success", title: "Record set deleted" });
      void qc.invalidateQueries({ queryKey: ["dns-records", name] });
    },
    onError: (e) => notify({ kind: "error", title: "Delete failed", body: e instanceof Error ? e.message : String(e) }),
  });

  const cols: DataListColumn<RRSet>[] = [
    { key: "name", label: "Name", sortable: true, render: (r) => <span className="font-mono text-[12.5px]">{r.name}</span> },
    { key: "type", label: "Type", sortable: true, render: (r) => <StatusPill tone="neutral">{r.type}</StatusPill> },
    { key: "ttl", label: "TTL", sortable: true, value: (r) => r.ttl },
    {
      key: "records",
      label: "Value",
      value: (r) => contentOf(r).join(" "),
      // A record set can hold several values and DNS treats them as one unit — deleting the set
      // removes all of them. Showing one line per value makes that visible before the click.
      render: (r) => (
        <div className="space-y-0.5">
          {contentOf(r).map((c, i) => (
            <div key={i} className="break-all font-mono text-[11.5px]">{c}</div>
          ))}
        </div>
      ),
    },
    {
      key: "actions",
      label: "",
      render: (r) => (
        <div className="flex justify-end">
          {/* NS and SOA at the apex are what make the zone resolvable at all; removing them
              takes the domain off the internet rather than changing a record. */}
          <Button
            size="sm"
            variant="danger"
            disabled={r.type === "SOA" || (r.type === "NS" && r.name === name)}
            onClick={() => setDel(r)}
          >
            {r.type === "SOA" || (r.type === "NS" && r.name === name) ? "structural" : "Delete"}
          </Button>
        </div>
      ),
    },
  ];

  const actual = ns.data?.actual ?? [];
  const planned = ns.data?.plan?.nameservers ?? [];
  const nsMatch =
    planned.length > 0 &&
    planned.every((p) => actual.some((a) => a.replace(/\.$/, "") === p.replace(/\.$/, "")));

  return (
    <div>
      <PageHeader
        title={name}
        crumbs={[{ label: "DNS zones", to: "/dns/zones" }, { label: name }]}
        actions={
          <Button variant="accent" onClick={create.open}>
            <Plus size={14} /> Add record
          </Button>
        }
      />

      <Section title="Delegation">
        <StateView loading={ns.isLoading} error={ns.error} errorContext="nameservers">
          <>
            {/* What the registrar actually publishes, against what this platform expects. A zone
                edited here that is delegated elsewhere accepts every change and serves none of
                them. */}
            {planned.length > 0 && (
              <div className="mb-2">
                <StatusPill tone={nsMatch ? "ok" : "bad"}>
                  {nsMatch ? "delegated to this platform" : "NOT delegated here — edits will not take effect"}
                </StatusPill>
              </div>
            )}
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[12.5px]">
              <dt className="text-muted">Published at the registrar</dt>
              <dd className="font-mono">{actual.join(", ") || "—"}</dd>
              <dt className="text-muted">This platform expects</dt>
              <dd className="font-mono">{planned.join(", ") || "—"}</dd>
            </dl>
          </>
        </StateView>
      </Section>

      <ZoneChecks zone={name} />

      <Section title="Records" className="mt-6" hint={recs.data ? `${recs.data.total}` : undefined} bodyClassName="p-0">
        <div className="p-4">
          <DataList
            items={recs.data?.records ?? []}
            columns={cols}
            getRowKey={(r) => `${r.name}/${r.type}`}
            searchable
            searchPlaceholder="Search records…"
            loading={recs.isLoading}
            error={recs.error}
            errorContext="DNS records"
            emptyMessage="No records."
          />
        </div>
      </Section>

      {create.dialog}
      <ConfirmDialog
        open={del !== null}
        onOpenChange={(o) => !o && setDel(null)}
        title={`Delete ${del?.name} ${del?.type}?`}
        destructive
        confirmLabel="Delete record set"
        pending={remove.isPending}
        onConfirm={() => {
          if (del) remove.mutate(del);
          setDel(null);
        }}
        body={
          <p>
            DNS deletes the whole SET, so all {contentOf(del ?? ({ records: [] } as unknown as RRSet)).length} value(s)
            for this name and type go together. Resolvers may keep serving the old answer until the TTL
            ({del?.ttl}s) expires.
          </p>
        }
      />
    </div>
  );
}

// Two questions a zone page cannot answer from its record list alone, and both were unreachable:
// does the INTERNET agree this zone is ours, and do the cluster's own names point at the right
// addresses.
function ZoneChecks({ zone }: { zone: string }) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();

  const verify = useQuery({
    queryKey: ["zone-verify", zone],
    queryFn: () =>
      client.apiFetch<{
        verdict?: string; zone?: string; public?: boolean; delegated_to?: string[] | null;
        nameservers?: string[] | null; local_serial?: number;
        checks?: { name?: string; check?: string; ok?: boolean; detail?: string }[] | null;
      }>(`/dns/zones/${encodeURIComponent(zone)}/verify`),
    retry: false,
  });
  const cluster = useQuery({
    queryKey: ["zone-cluster-records", zone],
    queryFn: () =>
      client.apiFetch<{
        records?: { name?: string; type?: string; content?: string; ok?: boolean }[] | null;
        drift?: boolean; node_addresses?: string[] | null; note?: string; zone?: string;
      }>(`/dns/zones/${encodeURIComponent(zone)}/cluster-records`),
    retry: false,
  });

  const republish = () =>
    client
      .apiFetch(`/dns/zones/${encodeURIComponent(zone)}/cluster-records`, { method: "POST" })
      .then(() => {
        notify({ kind: "success", title: "Cluster records republished" });
        void qc.invalidateQueries({ queryKey: ["zone-cluster-records", zone] });
        void qc.invalidateQueries({ queryKey: ["zone-records", zone] });
      })
      .catch((e: unknown) => notify({ kind: "error", title: "Could not republish", body: e instanceof Error ? e.message : String(e) }));

  const v = verify.data;

  return (
    <>
      <Section
        title="Is this zone really ours"
        className="mt-6"
        actions={v?.verdict ? <StatusPill tone={/^(ok|good|pass)/i.test(v.verdict) ? "ok" : "warn"}>{v.verdict}</StatusPill> : undefined}
      >
        <StateView loading={verify.isLoading} error={verify.error} errorContext="the delegation check">
          {/* Delegation is the one fact a record list cannot show. A zone can be perfectly
              configured here and delegated somewhere else entirely, in which case nothing we
              write is ever read. */}
          <div className="grid gap-x-8 gap-y-1 text-[12.5px] sm:grid-cols-2">
            <Row label="Answering on the internet" value={(v?.delegated_to ?? []).join(", ") || "nobody"} />
            <Row label="Should be" value={(v?.nameservers ?? []).join(", ") || "—"} />
            <Row label="Visible publicly" value={v?.public ? "yes" : "no"} />
            <Row label="Serial here" value={String(v?.local_serial ?? "—")} />
          </div>
          {(v?.checks?.length ?? 0) > 0 && (
            <ul className="mt-3 space-y-1">
              {(v?.checks ?? []).map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-[12.5px]">
                  <span className={c.ok ? "text-ok" : "text-crit"}>{c.ok ? "✓" : "✗"}</span>
                  <span>
                    {(c.name ?? c.check ?? "check").replace(/_/g, " ")}
                    {c.detail && <span className="ml-2 text-[11.5px] text-muted">{c.detail}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </StateView>
      </Section>

      <Section
        title="Cluster records"
        className="mt-6"
        actions={<Button size="sm" onClick={() => void republish()}>Republish</Button>}
      >
        <StateView loading={cluster.isLoading} error={cluster.error} errorContext="the cluster records">
          <div className="max-w-3xl text-[12px] leading-relaxed text-muted">
            {cluster.data?.note ?? "The apex, www, mail and webmail names this platform manages for the zone."}
          </div>
          {cluster.data?.drift && (
            <div className="mt-2 text-[12.5px] text-warn">
              These do not match the cluster&apos;s current addresses
              {(cluster.data?.node_addresses?.length ?? 0) > 0 && <> ({(cluster.data?.node_addresses ?? []).join(", ")})</>} —
              republishing rewrites them.
            </div>
          )}
          <ul className="mt-3 space-y-0.5 text-[12.5px]">
            {(cluster.data?.records ?? []).map((r, i) => (
              <li key={i} className="font-mono">
                <span className={r.ok === false ? "text-crit" : ""}>{r.name} {r.type} {r.content}</span>
              </li>
            ))}
            {(cluster.data?.records?.length ?? 0) === 0 && <li className="font-sans text-muted">None published.</li>}
          </ul>
        </StateView>
      </Section>
    </>
  );
}

interface PlatformNS {
  nameservers: string[] | null;
  stored?: boolean;
  effective?: string[] | null;
  note?: string;
  zones_out_of_sync?: string[] | null;
  zones?: {
    zone: string;
    serves: string[] | null;
    matches?: boolean;
    delegated_to: string[] | null;
    delegation?: string;
    left_at_registrar?: string[] | null;
  }[] | null;
}

// The platform's nameserver list is ONE cluster setting: the extra names every zone publishes beside
// the live nodes' own names. Each zone is shown with what it serves here and what its parent still
// delegates to, because the delegation is the half only the registrar can change.
function PlatformNameservers() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const q = useQuery({
    queryKey: ["dns-platform-ns"],
    queryFn: () => client.apiFetch<PlatformNS>("/dns/platform/nameservers"),
    retry: false,
  });
  const [draft, setDraft] = React.useState<string | null>(null);
  const save = useMutation({
    mutationFn: (names: string[]) =>
      client.apiFetch<PlatformNS>("/dns/platform/nameservers", { method: "PUT", body: JSON.stringify({ nameservers: names }) }),
    onSuccess: (r) => {
      setDraft(null);
      notify({ kind: "success", title: "Nameservers saved for the cluster", body: r?.note });
      void qc.invalidateQueries({ queryKey: ["dns-platform-ns"] });
    },
    onError: (e: unknown) => notify({ kind: "error", title: "Could not save them", body: e instanceof Error ? e.message : String(e) }),
  });
  const d = q.data;
  const current = (d?.nameservers ?? []).join(", ");
  const editing = draft !== null;
  const names = (draft ?? "").split(/[\s,]+/).map((n) => n.trim()).filter(Boolean);

  return (
    <Section
      title="Platform nameservers"
      className="mt-6"
      actions={
        editing ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
            <Button size="sm" variant="accent" disabled={save.isPending} onClick={() => save.mutate(names)}>
              Save for the cluster
            </Button>
          </>
        ) : (
          <Button size="sm" onClick={() => setDraft(current)}>Edit</Button>
        )
      }
    >
      <StateView loading={q.isLoading} error={q.error} errorContext="the platform nameservers">
        <div className="max-w-3xl text-[12px] leading-relaxed text-muted">{d?.note}</div>
        {editing ? (
          <div className="mt-3 max-w-xl">
            <Field
              mono
              label="Extra names, beside every live node's own name (empty = the nodes only)"
              value={draft ?? ""}
              placeholder="ns1.example.net, ns2.example.net"
              onChange={(v) => setDraft(v)}
            />
          </div>
        ) : (
          <div className="mt-3 grid gap-x-8 gap-y-1 text-[12.5px] sm:grid-cols-2">
            <Row label="Setting" value={d?.stored ? current : "not set — the live nodes only"} />
            <Row label="Every zone publishes" value={(d?.effective ?? []).join(", ") || "—"} />
          </div>
        )}
        <table className="mt-4 w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-muted">
              <th className="py-1 pr-4 font-normal">Zone</th>
              <th className="py-1 pr-4 font-normal">Serves (NS here)</th>
              <th className="py-1 pr-4 font-normal">Delegated to (public)</th>
              <th className="py-1 font-normal">Left at the registrar</th>
            </tr>
          </thead>
          <tbody>
            {(d?.zones ?? []).map((z) => (
              <tr key={z.zone} className="border-t border-line align-top">
                <td className="py-1 pr-4 font-mono">
                  {z.zone}
                  {z.matches === false && <span className="ml-2"><StatusPill tone="warn">out of step</StatusPill></span>}
                </td>
                <td className="py-1 pr-4 font-mono">{(z.serves ?? []).join(", ") || "—"}</td>
                <td className="py-1 pr-4 font-mono" title={z.delegation}>{(z.delegated_to ?? []).join(", ") || "not readable"}</td>
                <td className={"py-1 font-mono " + ((z.left_at_registrar?.length ?? 0) > 0 ? "text-warn" : "text-muted")}>
                  {(z.left_at_registrar ?? []).join(", ") || "nothing"}
                </td>
              </tr>
            ))}
            {(d?.zones?.length ?? 0) === 0 && (
              <tr><td colSpan={4} className="py-1 text-muted">No zones on this node.</td></tr>
            )}
          </tbody>
        </table>
      </StateView>
    </Section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-line py-1">
      <span className="text-muted">{label}</span>
      <span className="text-right font-mono text-[12px]">{value}</span>
    </div>
  );
}
